import React, { useState } from 'react';
import { Logger } from '@saviynt/common';
import {
  AlertBanner,
  AlertBannerProvider,
  Button,
  ButtonIcon,
  Icon,
  Menu,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import {
  getBaseUrlObNextGen,
  getObNextGenApiUrlLists,
} from '../../constants/obNextGenConstants';
import testIds from '../../constants/testIds';
import useLocalization from '../../hooks/use-localize';
import { usePatchDiscoveryStatusMutation } from '../DisableCMDBDiscoveredAppsModal/data-store/changeDiscoverAppStatusSlice';
import DisableCMDBDiscoveredAppsModal from '../DisableCMDBDiscoveredAppsModal/DisableCMDBDiscoveredAppsModal';
import DiscoveryImport from '../DiscoveryImport/components/DiscoveryImport';

import './ManageCMDBDiscoveredAppsHeader.css';

export interface DiscoveryConfigHeaderDetails {
  /** Instance name */
  name: string;
  /** Discovery source (e.g., ServiceNow) */
  discoverySource: string;
  /** Icon file name */
  imageName: string;
  /** Current status */
  status: string;
  id: number;
  appTypeKey: number;
}

interface ManageCMDBDiscoveredAppsHeaderProps {
  /** Metadata details of the selected discovery configuration */
  headerDetails: DiscoveryConfigHeaderDetails;
}

/**
 * Header Component
 */
function ManageCMDBDiscoveredAppsHeader({
  headerDetails,
}: ManageCMDBDiscoveredAppsHeaderProps): JSX.Element {
  const formatMessage = useLocalization();

  const initialOptionsWithIcons = [
    {
      label: formatMessage('INTEGRATIONS.BUTTON.DISABLE'),
      value: 'Disable',
    },
  ];

  const [postStatus, { isLoading: isStatusChangeLoading }] =
    usePatchDiscoveryStatusMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [isStartImportModalOpen, setIsStartImportModalOpen] = useState(false);
  const [isImportRunning, setIsImportRunning] = useState(false);

  const [alertBannerObj, setAlertBannerObj] = useState<{
    kind: 'Success' | 'Critical';
    colorTheme: 'Success' | 'Critical';
    title: string;
    description: string;
    linkProps: { color: string; dataTheme: string };
  }>({
    kind: 'Success',
    colorTheme: 'Success',
    title: '',
    description: '',
    linkProps: { color: 'neutral', dataTheme: 'dark' },
  });
  const [isAlertBannerVisible, setIsAlertBannerVisible] = useState(false);

  const { GET_APPLICATION_ICONS } = getObNextGenApiUrlLists();

  const handleChange = (selectedOption: { label: string; value: string }) => {
    Logger.log('selectedOption', selectedOption);

    if (selectedOption.value === 'Disable') {
      setIsOpen(true);
    }
  };

  const changeStatusCallback = (
    resp: { data?: unknown; error?: unknown },
    status: 'enable' | 'disable'
  ) => {
    // map each status to the right message keys
    const messageKeys = {
      enable: {
        button: 'INTEGRATIONS.ENABLING',
        buttonVerb: 'Enabling',
        successAlert:
          'INTEGRATIONS.CMDB_HEADER.ALERT.ENABLE_SUCCESS_DESCRIPTION',
        errorAlert: 'INTEGRATIONS.CMDB_HEADER.ALERT.ENABLE_FAILED_TITLE',
        successVerb: 'Enabled successfully.',
        errorVerb: 'Enabling Failed',
      },
      disable: {
        button: 'INTEGRATIONS.BUTTON.DISABLING',
        buttonVerb: 'Disabling',
        successAlert:
          'INTEGRATIONS.CMDB_HEADER.ALERT.DISABLE_SUCCESS_DESCRIPTION',
        errorAlert: 'INTEGRATIONS.CMDB_HEADER.ALERT.DISABLE_FAILED_TITLE',
        successVerb: 'Disabled successfully.',
        errorVerb: 'Disabling Failed',
      },
    } as const;

    const {
      button,
      buttonVerb,
      successAlert,
      errorAlert,
      successVerb,
      errorVerb,
    } = messageKeys[status];

    if (resp.error) {
      setAlertBannerObj({
        kind: 'Critical',
        colorTheme: 'Critical',
        title: formatMessage(errorAlert, errorVerb),
        description:
          resp.error &&
          typeof resp.error === 'object' &&
          resp.error !== null &&
          'data' in resp.error &&
          resp.error.data &&
          typeof resp.error.data === 'object' &&
          resp.error.data !== null &&
          'message' in resp.error.data &&
          typeof resp.error.data.message === 'string'
            ? resp.error.data.message
            : formatMessage(errorAlert, errorVerb),
        linkProps: { color: 'neutral', dataTheme: 'dark' },
      });
    } else {
      setAlertBannerObj({
        kind: 'Success',
        colorTheme: 'Success',
        title: formatMessage(button, buttonVerb),
        description: `${headerDetails.name} ${formatMessage(
          successAlert,
          successVerb
        )}`,
        linkProps: { color: 'neutral', dataTheme: 'dark' },
      });
    }

    setIsOpen(false);
    setIsAlertBannerVisible(true);
  };

  const closeAlertBanner = () => setIsAlertBannerVisible(false);
  const startImport = () => setIsStartImportModalOpen(true);
  const closeStartImportModal = () => setIsStartImportModalOpen(false);

  const enableApp = async () => {
    const resp = await postStatus({
      id: headerDetails.id,
      type: 'Discovery_Source',
      status: 'enable',
    });

    changeStatusCallback(resp, 'enable');
  };

  return (
    <div className={isAlertBannerVisible ? 'cmdb-header-wrapper' : ''}>
      <AlertBannerProvider>
        <AlertBanner
          className='cmdb-header-container-alertBanner'
          colorTheme={alertBannerObj.colorTheme}
          title={alertBannerObj.title}
          description={alertBannerObj.description}
          isVisible={isAlertBannerVisible}
          onCancel={closeAlertBanner}
          dataTestId={testIds.CMDB_HEADER_ALERT_BANNER}
        />
      </AlertBannerProvider>

      <div
        className='cmdb-header-container'
        data-testid={testIds.CMDB_HEADER_CONTAINER}>
        <button
          type='button'
          className='Discovery-config-backButton'
          data-testid={testIds.CMDB_HEADER_BACK_BUTTON}
          aria-label={formatMessage(
            'INTEGRATIONS.CMDB_HEADER.BACK_BUTTON_ARIA_LABEL'
          )}
          onClick={() => window.history.back()}>
          <Icon kind='ChevronLeftSmall' />
        </button>

        <img
          className='cmdb-header-container-Image'
          data-testid={testIds.CMDB_HEADER_IMAGE}
          src={`${getBaseUrlObNextGen()}${GET_APPLICATION_ICONS}/${
            headerDetails.imageName
          }`}
          alt={formatMessage('INTEGRATIONS.CMDB_HEADER.IMAGE_ALT')}
        />

        <div className='Discovery-config-title'>
          <Typography kind='h1'>{headerDetails.name}</Typography>
          <Typography kind='body1'>{headerDetails.discoverySource}</Typography>
        </div>

        <div className='cmdb-header-container-actions'>
          {headerDetails.status !== 'Disabled' && (
            <Button
              type='button'
              kind='outlined'
              onClick={startImport}
              dataTestId={testIds.CMDB_HEADER_RUN_MANUALLY_BUTTON}>
              {formatMessage('INTEGRATIONS.CMDB_HEADER.RUN_MANUALLY_BUTTON')}
            </Button>
          )}

          {headerDetails.status !== 'Disabled' && (
            <Menu
              options={initialOptionsWithIcons}
              onChange={handleChange}
              dataTestId={testIds.CMDB_HEADER_MORE_MENU}
              trigger={
                <ButtonIcon
                  size='medium'
                  kind='ghost'
                  icon={<Icon kind='MoreVertical' />}
                />
              }
            />
          )}

          {headerDetails.status === 'Disabled' && (
            <Button
              type='button'
              kind='outlined'
              isLoading={isStatusChangeLoading}
              loaderText={formatMessage('INTEGRATIONS.ENABLING')}
              onClick={enableApp}
              dataTestId={testIds.CMDB_HEADER_ENABLE_BUTTON}>
              {formatMessage('INTEGRATIONS.BUTTON.ENABLE')}
            </Button>
          )}
        </div>

        <DisableCMDBDiscoveredAppsModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          id={headerDetails.id}
          name={headerDetails.name}
          type='Discovery_Source'
          status='disable'
          changeStatusCallback={changeStatusCallback}
        />

        <ModalIsland
          kind='default'
          headerIcon={
            <Icon kind='ManageOverview' size='large' aria-hidden='true' />
          }
          title={formatMessage('INTEGRATIONS.MODAL_TITLE.DISCOVERY')}
          isOpen={isStartImportModalOpen}
          onClose={isImportRunning ? undefined : closeStartImportModal}
          isOutsideClickEnabled={!isImportRunning}
          isCloseOnEscapeKeyEnabled={!isImportRunning}
          isCloseButtonVisible={!isImportRunning}
          dataTestId={testIds.CMDB_HEADER_IMPORT_MODAL}
          contentBody={
            <DiscoveryImport
              appTypeKey={headerDetails.appTypeKey}
              iobAppKey={headerDetails.id}
              iconLink={headerDetails.imageName}
              closeStartImportModal={closeStartImportModal}
              onImportStatusChange={setIsImportRunning}
            />
          }
        />
      </div>
    </div>
  );
}

export default ManageCMDBDiscoveredAppsHeader;
